import type { SectionType, ResourceType, OriginApp, ShowcaseStatus } from './constants';

export interface Showcase {
  id: string;
  user_id: string;
  team_id: string | null;
  title: string;
  slug: string;
  status: ShowcaseStatus;
  template_id: string;
  primary_brand_colour: string | null;
  logo_url: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  office_name: string | null;
  office_address: string | null;
  hero_resource_id: string | null;
  active_space_id: string | null;
  published_url: string | null;
  qr_code_url: string | null;
  section_order: SectionType[];
  enabled_sections: SectionType[];
  password: string | null;
  logo_size: string;
  property_status: string | null;
  agent_id: string | null;
  notify_agent: boolean;
  notify_owner: boolean;
  notify_emails: string[];
  sub_brand_id: string | null;
  hosting_plan: ShowcaseHostingPlan | null;
  hosting_expires_at: string | null;
  created_at: string;
  updated_at: string;
  published_at: string | null;
}

export type ShowcaseHostingPlan = '7d' | '30d' | '6mo' | '12mo' | 'subscription';

export function isShowcaseLive(showcase: Pick<Showcase, 'status' | 'hosting_expires_at'>): boolean {
  if (showcase.status !== 'published') return false;
  return !showcase.hosting_expires_at || new Date(showcase.hosting_expires_at).getTime() > Date.now();
}

export interface ShowcaseProperty {
  showcase_id: string;
  property_title: string | null;
  address_line_1: string | null;
  address_line_2: string | null;
  town_city: string | null;
  county_state: string | null;
  postcode_zip: string | null;
  country: string | null;
  price_display: string | null;
  property_type: string | null;
  bedrooms: number | null;
  bathrooms: number | null;
  reception_rooms: number | null;
  internal_area: string | null;
  tenure: string | null;
  short_headline: string | null;
  summary_description: string | null;
  full_description: string | null;
  key_features: string[];
  latitude: number | null;
  longitude: number | null;
  epc_current: number | null;
  epc_potential: number | null;
}

export interface ShowcaseResource {
  id: string;
  showcase_id: string;
  type: ResourceType;
  title: string | null;
  source_url: string | null;
  embed_url: string | null;
  thumbnail_url: string | null;
  metadata_json: Record<string, any> | null;
  sort_order: number;
  is_enabled: boolean;
  origin_app: OriginApp | null;
  origin_resource_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface ShowcaseTemplate {
  id: string;
  name: string;
  slug: string;
  description: string;
  preview_image_url: string | null;
  category: string;
  is_active: boolean;
  default_section_order: SectionType[];
  default_enabled_sections: SectionType[];
  supported_resource_types: ResourceType[];
}

export interface ShowcaseSeo {
  showcase_id: string;
  custom_meta_title: string | null;
  custom_meta_description: string | null;
  og_image_url: string | null;
  canonical_url: string | null;
  noindex: boolean;
  analytics_json: Record<string, any> | null;
}

export interface ShowcaseAgent {
  id: string;
  user_id: string;
  team_id: string | null;
  name: string;
  email: string;
  phone: string | null;
  photo_url: string | null;
  office_name: string | null;
  office_address: string | null;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export interface ShowcaseLead {
  id: string;
  showcase_id: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  message: string | null;
  source: 'contact_form' | 'ai_assistant' | 'request_viewing';
  created_at: string;
}

export interface ShowcaseAiSession {
  id: string;
  showcase_id: string;
  visitor_id: string;
  messages: AiMessage[];
  lead_capture_requested: boolean;
  created_at: string;
  updated_at: string;
}

export interface AiMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

export interface PublicShowcaseData {
  showcase: Showcase;
  property: ShowcaseProperty;
  resources: ShowcaseResource[];
  seo: ShowcaseSeo | null;
  template: ShowcaseTemplate;
}

export type AppView =
  | 'LOADING'
  | 'LOGIN'
  | 'DASHBOARD'
  | 'CREATE'
  | 'EDITOR'
  | 'LEADS'
  | 'ANALYTICS'
  | 'AGENTS';

export type WizardStep = 'details' | 'template' | 'branding';
